import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Checkbox, Flex, Input, Form, message, Space, Popover, Modal } from "antd";
import {
  login,
  loginByPhone,
  getPhoneVerifyCode,
  getBindEmailCode,
  postWxQrcode,
  postWxQrcodeStatus,
  loginBindWx,
  resetPassword,
} from '@/apis/login';
import { getUserInfo } from '@/apis/user';
import { DataContext } from "@/context";
import QR_CODE from "@/assets/images/login/qrcode.jpg";
import WEIXIN from "@/assets/images/weixin.svg";
import EyePng from "@/assets/images/login/eye.png";
import HideEyePng from "@/assets/images/login/hide_eye.png";
import SUCCESS from './images/success.png';
import { ReloadOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { recordEventRecord } from '@/apis/record';
import { useLocation } from 'react-router-dom';
import REGISTER_SUCCESS from '@/assets/login/register_success.png';

const LOGIN_LOGO = 'https://ps-cdn.cemeta.cn/configs/frontend/20250917/3.png';

interface Props {
  open: Boolean;
  propsSetOpen?: (value: boolean) => any;
  propsSetIsLogin?: (value: string) => any;
}

const formItemLayout = {
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
};

const emailPattern = /^[\w.%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const phonePattern = /^(?:(?:\+|00)86)?1(?:(?:3[\d])|(?:4[5-9])|(?:5[0-35-9])|(?:6[2567])|(?:7[0-8])|(?:8[\d])|(?:9[0-35-9]))\d{8}$/;
const passwordPattern = /^(?=.*\d)(?=.*[a-zA-Z]).*$/;

const Login: React.FC<Props> = (props: Props) => {
  const { open, propsSetOpen, propsSetIsLogin } = props;
  const timer = useRef(0);
  const bindTimer = useRef(0);
  const resetPassTimer = useRef(0);
  const timerQrcode = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { userInfo, setContextValue } = useContext(DataContext);

  const searchParamsString = window.location.href.split('?')[1] || '';
  const searchParams = new URLSearchParams(searchParamsString);
  const source = searchParams.get('s');
  const detail = searchParams.get('d');

  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [resetPassForm] = Form.useForm();
  const [bindForm] = Form.useForm();

  const [isWX, setIsWX] = useState(false);
  const [isVerifyCode, setIsVerifyCode] = useState(true);
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [isWxRegister, setIsWxRegister] = useState(false);
  const [checked, setChecked] = useState(true);
  const [countDown, setCountDown] = useState(60);

  const [wxObj, setWxObj] = useState<{ base64?: string; sid?: string }>({});
  const [qrcodeStatus, setQrcodeStatus] = useState('');

  const [resetPassVisible, setResetPassVisible] = useState(false);
  const [isResetPassForm, setIsResetPassForm] = useState(false);
  const [resetPassCountDown, setResetPassCountDown] = useState(60);
  const [bindAccountVisible, setBindAccountVisible] = useState(false);
  const [bindModalStep, setBindModalStep] = useState<'choice' | 'form'>('choice');
  const [bindIsVerifyCode, setBindIsVerifyCode] = useState(true);
  const [bindCountDown, setBindCountDown] = useState(60);
  const location = useLocation();
  const [successOpen, setSuccessOpen] = useState(false);
  const [successOpenObj, setSuccessOpenObj] = useState({
    title: '',
    type: '',
    text: '',
    url: '',
  });

  const queryParams = new URLSearchParams(location.search);
  const queryY = queryParams?.get('y')

  useEffect(() => {
    const rememberPassword = localStorage.getItem('rememberPassword');
    setChecked(rememberPassword !== 'false' || !rememberPassword);
  }, []);

  useEffect(() => {
    if (!(userInfo?.user_id === 0 || userInfo?.user_id) && open && !isWxRegister) {
      const userForm = localStorage.getItem('userForm') || localStorage.getItem('userLogin');
      if (userForm) {
        try {
          const obj = JSON.parse(userForm);
          if (obj?.password) {
            setIsVerifyCode(false);
          } else {
            setIsVerifyCode(true);
          }
          
          form.setFieldsValue({
            ...form.getFieldsValue(),
            emailOrPhone: obj.emailOrPhone || obj.email || obj.phone_number || '',
            password: obj.password || '',
          });
        } catch (err) {
          console.error(err);
        }
      }
    } else {
      form.setFieldsValue({
          ...form.getFieldsValue(),
            emailOrPhone: '',
            password: '',
      });
    }
  }, [open, userInfo?.user_id, isWxRegister, form]);

  useEffect(() => {
    if (countDown !== 60) {
      timer.current = window.setTimeout(() => {
        if (countDown <= 0) {
          timer.current && clearTimeout(timer.current);
          setCountDown(60);
        } else {
          setCountDown(countDown - 1);
        }
      }, 1000);
    }
    return () => {
      timer.current && clearTimeout(timer.current);
    };
  }, [countDown]);

  useEffect(() => {
    if (bindCountDown !== 60) {
      bindTimer.current = window.setTimeout(() => {
        if (bindCountDown <= 0) {
          bindTimer.current && clearTimeout(bindTimer.current);
          setBindCountDown(60);
        } else {
          setBindCountDown(bindCountDown - 1);
        }
      }, 1000);
    }
    return () => {
      bindTimer.current && clearTimeout(bindTimer.current);
    };
  }, [bindCountDown]);

  useEffect(() => {
    if (resetPassCountDown !== 60) {
      resetPassTimer.current = window.setTimeout(() => {
        if (resetPassCountDown <= 0) {
          resetPassTimer.current && clearTimeout(resetPassTimer.current);
          setResetPassCountDown(60);
        } else {
          setResetPassCountDown(resetPassCountDown - 1);
        }
      }, 1000);
    }
    return () => {
      resetPassTimer.current && clearTimeout(resetPassTimer.current);
    };
  }, [resetPassCountDown]);

  useEffect(() => {
    if (isWX) {
      getNewQrcode();
    } else {
      setIsWxRegister(false);
      setWxObj({});
      setQrcodeStatus('');
      if (timerQrcode.current) {
        clearTimeout(timerQrcode.current);
        timerQrcode.current = null;
      }
    }
    return () => {
      if (timerQrcode.current) {
        clearTimeout(timerQrcode.current);
        timerQrcode.current = null;
      }
    };
  }, [isWX]);

  useEffect(() => {
    if (wxObj?.sid) {
      const pollStatus = async () => {
        const result = await postWxQrcodeStatus({ sid: wxObj.sid });
        if (result?.status === 'pending') {
          timerQrcode.current = setTimeout(pollStatus, 3000);
          return;
        }
        if (result?.status === 'success') {
          setQrcodeStatus('success');
          wxLoginSuccess(result);
        } else if (result?.status === 'invalid') {
          setQrcodeStatus('invalid');
        } else if (result?.status === 'need_bind') {
          setQrcodeStatus('need_bind');
        } else {
          setQrcodeStatus('');
        }
        if (timerQrcode.current) {
          clearTimeout(timerQrcode.current);
          timerQrcode.current = null;
        }
      };
      pollStatus();
    }
    return () => {
      if (timerQrcode.current) {
        clearTimeout(timerQrcode.current);
        timerQrcode.current = null;
      }
    };
  }, [wxObj?.sid]);

  useEffect(() => {
    if (wxObj?.sid && qrcodeStatus === 'need_bind') {
      setBindModalStep('choice');
      setBindIsVerifyCode(true);
      bindForm.resetFields();
      setBindAccountVisible(true);
    }
  }, [qrcodeStatus, wxObj?.sid, bindForm]);

  useEffect(() => {
    if (isWxRegister) {
      setIsVerifyCode(true)
    }
  }, [isWxRegister])


  useEffect(() => {
      if (isVerifyCode && queryY) {
        form.setFieldsValue({
          ...form.getFieldsValue(),
          invite_code: queryY
        })
        setShowInviteCode(true)
      }
  }, [isVerifyCode, queryY])

  const getNewQrcode = async () => {
    setBindAccountVisible(false);
    setBindModalStep('choice');
    const res = await postWxQrcode();
    if (res?.base64 && res?.sid) {
      setWxObj(res);
      setQrcodeStatus('pending');
    } else {
      setQrcodeStatus('error');
    }
  };

  const debounceGetNewQrcode = debounce(() => {
    getNewQrcode();
  }, 500);

  const timeCountDown = () => {
    setCountDown(countDown - 1);
  };

  const loginSuccess = (res: any) => {
    if (res?.token) {
      localStorage.setItem('userToken', res.token);
    }
    localStorage.setItem('userInfo', JSON.stringify(res));
    setContextValue((contextValue) => ({
      ...contextValue,
      userInfo: {
        ...res,
        user_id: '' + res?.user_id + '_' + new Date().getTime(),
      },
    }));
    messageApi.open({ type: 'success', content: '登录成功' });
    propsSetOpen?.(false);
  };

  const wxLoginSuccess = async (res: any) => {
    if (res?.token) {
      const result = await getUserInfo(res.token);
      localStorage.setItem('userToken', res.token);
      if (result?.user_id) {
        setContextValue((contextValue) => ({
          ...contextValue,
          userInfo: {
            ...result,
            user_id: '' + result?.user_id + '_' + new Date().getTime(),
          },
        }));
        localStorage.setItem('userInfo', JSON.stringify({ ...result, token: res.token }));
        messageApi.open({ type: 'success', content: '登录成功' });
        propsSetOpen?.(false);
      } else {
        messageApi.open({ type: 'error', content: '获取用户信息失败！' });
      }
    } else {
      messageApi.open({ type: 'error', content: '缺少关键用户信息！' });
    }
  };

  const saveUserForm = (emailOrPhone: string, password: string) => {
    if (checked && !isVerifyCode) {
      localStorage.setItem('userForm', JSON.stringify({ emailOrPhone, password }));
    } else {
      localStorage.setItem('userForm', JSON.stringify({ emailOrPhone, password: '' }));
    }
  };

  const handleResetPassCode = (res: any, emailOrPhone: string) => {
    setIsResetPassForm(false);
    setResetPassCountDown(60);
    resetPassForm.setFieldsValue({
      ...form.getFieldsValue(),
      emailOrPhone,
      verify_code: '',
      password: '',
    });
    setResetPassVisible(true);
    if (res?.token) {
      localStorage.setItem('userToken', res.token);
    }
  };

  const handleCloseResetPassModal = () => {
    setResetPassVisible(false);
    setIsResetPassForm(false);
    setResetPassCountDown(60);
    resetPassForm.resetFields();
  };

  const handleResetPassVerifyCode = async () => {
    if (resetPassCountDown !== 60) {
      messageApi.open({ type: 'warning', content: `${resetPassCountDown}秒后再试` });
      return;
    }
    const emailOrPhone = resetPassForm.getFieldValue('emailOrPhone')?.trim();
    if (!emailOrPhone) {
      messageApi.open({ type: 'warning', content: '请填写手机号或邮箱' });
      return;
    }
    const isEmail = emailPattern.test(emailOrPhone);
    const isPhone = phonePattern.test(emailOrPhone);
    if (!isEmail && !isPhone) {
      messageApi.open({ type: 'warning', content: '请填写正确的手机号或邮箱' });
      return;
    }
    try {
      if (isEmail) {
        await getBindEmailCode({ email: emailOrPhone, code_type: 'init' });
        messageApi.open({ type: 'success', content: '邮箱验证码已发送，请注意查收' });
      } else {
        await getPhoneVerifyCode({
          phone_number: emailOrPhone,
          country_code: '+86',
          code_type: 'init',
        }).then(() => {
            messageApi.open({
              type: 'success',
              content: '短信验证码已发送，请注意查收',
            });
            setResetPassCountDown(59);
          })
          .catch(err => {
            if (err?.code === 5003) {
              messageApi.open({
                type: 'info',
                content: err?.err_msg || '联系客服获取验证码',
              });
            } else {
              messageApi.open({
                type: 'error',
                content: err?.err_msg || '发送验证码失败，请稍后再试',
              });
            }
          })
      }
      
    } catch (error: any) {
      console.error(error);
      messageApi.open({ type: 'error', content: error });
    }
  };

  const handleClickVerifyCode = async () => {
    if (countDown !== 60) {
      messageApi.open({ type: 'warning', content: `${countDown}秒后再试` });
      return;
    }
    const emailOrPhone = form.getFieldValue('emailOrPhone')?.trim();
    if (isWxRegister) {
      if (!emailOrPhone) {
        messageApi.open({ type: 'warning', content: '请填写手机号' });
        return;
      }
    } else if (!emailOrPhone) {
      messageApi.open({ type: 'warning', content: '请填写手机号或邮箱' });
      return;
    }

    const isEmail = emailPattern.test(emailOrPhone);
    const isPhone = phonePattern.test(emailOrPhone);
    if (isWxRegister) {
      if (!isPhone) {
        messageApi.open({ type: 'warning', content: '请填写正确的手机号' });
        return;
      }
    } else if (!isEmail && !isPhone) {
      messageApi.open({ type: 'warning', content: '请填写正确的手机号或邮箱' });
      return;
    }

    try {
      if (isEmail && !isWxRegister) {
        await getBindEmailCode({ email: emailOrPhone, code_type: 'login' });
        messageApi.open({ type: 'success', content: '验证码已发送，请注意查收' });
      } else {
        await getPhoneVerifyCode({
          phone_number: emailOrPhone,
          country_code: '+86',
          code_type: 'login',
        }).then(() => {
            messageApi.open({
              type: 'success',
              content: '短信验证码已发送，请注意查收',
            });
            timeCountDown()
          })
          .catch(err => {
            if (err?.code === 5003) {
              messageApi.open({
                type: 'info',
                content: err?.err_msg || '联系客服获取验证码',
              });
            } else {
              messageApi.open({
                type: 'error',
                content: err?.err_msg || '发送验证码失败，请稍后再试',
              });
            }
          })
      }
    } catch (error: any) {
      console.error(error);
      messageApi.open({ type: 'error', content: error });
    }
  };

  const handleLogin = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const { emailOrPhone, password, verify_code, invite_code } = form.getFieldsValue();
    const account = emailOrPhone?.trim();
    const isEmail = emailPattern.test(account);
    const isPhone = phonePattern.test(account);

    if (source && detail) {
      recordEventRecord({
        event_name: 'haohui_source_login',
        source,
        event_id: detail,
      });
    }
    recordEventRecord({ event_name: 'haohui_login' });

    if (!isPhone && !isWxRegister) {
      try {
        const res = await login({
          email: account,
          ...(isVerifyCode ? { verify_code: verify_code?.trim() } : { password: password?.trim() }),
          ...(invite_code ? { invite_code: invite_code.trim() } : {}),
        });
        if (res?.code === 5001) {
          console.log('account', account)
          handleResetPassCode(res, account);
          return;
        }
        if (res) {
          if (res?.status === 'error') {
            messageApi.open({ type: 'error', content: res?.err_msg });
            return
          } 
          saveUserForm(account, password?.trim() || '');
          if (res?.user_reward) {
              const user_reword = res?.user_reward
              if (user_reword?.reward_event === 'register') {
                setSuccessOpen(true)
                setSuccessOpenObj({
                  title: '注册成功！',
                  type: 'register',
                  text: `🎉恭喜解锁新手礼包，算力有效期${user_reword?.reward_duration}`,
                  url: user_reword?.reward_url,
                });
              }
              if (user_reword?.reward_event === 'regress') {
                setSuccessOpen(true);
                setSuccessOpenObj({
                  title: '欢迎回来！',
                  type: 'regress',
                  text: `🎉欢迎回来！送您专属算力礼包，算力有效期${user_reword?.reward_duration}`,
                  url: user_reword?.reward_url,
                });
              }
           }
          loginSuccess(res);
        }
      } catch (error: any) {
        console.log(error)
        messageApi.open({ type: 'error', content: error });
      }
    } else {
      try {
        let res;
        if (isWxRegister) {
          res = await loginBindWx({
            sid: wxObj?.sid,
            phone_number: account,
            country_code: '+86',
            type: 'register',
            ...(isVerifyCode ? { verify_code: verify_code?.trim() } : { password: password?.trim() }),
          });
        } else {
          res = await loginByPhone({
            phone_number: account,
            country_code: '+86',
            ...(isVerifyCode ? { verify_code: verify_code?.trim() } : { password: password?.trim() }),
            ...(invite_code ? { invite_code: invite_code.trim() } : {}),
          });
        }
        if (res?.code === 5001) {
          handleResetPassCode(res, account);
          return;
        }
        if (res) { 
          saveUserForm(account, password?.trim() || '');
          if (res?.user_reward) {
            const user_reword = res?.user_reward
            if (user_reword?.reward_event === 'register') {
              setSuccessOpen(true)
              setSuccessOpenObj({
                title: '注册成功！',
                type: 'register',
                text: `🎉恭喜解锁新手礼包，算力有效期${user_reword?.reward_duration}`,
                url: user_reword?.reward_url,
              });
            }
            if (user_reword?.reward_event === 'regress') {
              setSuccessOpen(true);
              setSuccessOpenObj({
                title: '欢迎回来！',
                type: 'regress',
                text: `🎉欢迎回来！送您专属算力礼包，算力有效期${user_reword?.reward_duration}`,
                url: user_reword?.reward_url,
              });
            }
          }
          loginSuccess(res);
          setIsWX(false)
        }
      } catch (error: any) {
        console.error(error);
        messageApi.open({ type: 'error', content: error });
      }
    }
  };

  const handleResetPass = async () => {
    try {
      await resetPassForm.validateFields();
    } catch {
      return;
    }
    const { emailOrPhone, verify_code, password } = resetPassForm.getFieldsValue();
    const account = emailOrPhone?.trim();
    const isEmail = emailPattern.test(account);
    const isPhone = phonePattern.test(account);
    try {
      await resetPassword({
        ...(isEmail ? { email: account } : { phone_number: account, country_code: '+86' }),
        verify_code: verify_code?.trim(),
        new_password: password?.trim(),
      });
      messageApi.open({ type: 'success', content: '设置密码成功' });
      handleCloseResetPassModal();
      setIsVerifyCode(false);
    } catch (error: any) {
      console.error(error);
      messageApi.open({ type: 'error', content: error });
    }
  };

  const renderResetPassModalContent = () => {
    if (!isResetPassForm) {
      return (
        <>
          <div className="login-reset-pass-prompt">
            <span className="login-reset-pass-prompt-title">提示</span>
            <span className="login-reset-pass-prompt-text">该账号还未设置密码，</span>
            <span className="login-reset-pass-prompt-text">请先设置密码或选择验证码登录～</span>
          </div>
          <button
            type="button"
            className="login-bind-modal-btn-primary"
            onClick={() => {
              handleCloseResetPassModal();
              setIsVerifyCode(true);
            }}
          >
            验证码登录
          </button>
          <button
            type="button"
            className="login-bind-modal-btn-secondary"
            onClick={() => setIsResetPassForm(true)}
          >
            设置密码
          </button>
        </>
      );
    }

    return (
      <Form form={resetPassForm} layout="vertical" requiredMark={false}>
        <Form.Item
          name="emailOrPhone"
          label="账号"
          rules={[
            { required: true, message: '请填写手机号或邮箱' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const isEmail = emailPattern.test(value);
                const isPhone = phonePattern.test(value);
                if (!isEmail && !isPhone) {
                  return Promise.reject(new Error('请填写正确的手机号或邮箱'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input className="login-bind-form-input" disabled placeholder="请输入手机号/邮箱" />
        </Form.Item>
        <Form.Item
          label="验证码"
          name="verify_code"
          rules={[
            { required: true, message: '请填写验证码' },
            { len: 6, message: '请输入6位验证码' },
          ]}
        >
          <Input
            className="login-bind-form-input"
            placeholder="请输入验证码"
            suffix={
              resetPassCountDown === 60 ? (
                <span className="login-bind-get-code" onClick={handleResetPassVerifyCode}>
                  获取验证码
                </span>
              ) : (
                <span className="login-bind-get-code disabled">{resetPassCountDown}(s)后再试</span>
              )
            }
          />
        </Form.Item>
        <Form.Item
          label="密码"
          name="password"
          rules={[
            { required: true, message: '请填写密码' },
            { pattern: passwordPattern, message: '密码需要包含数字和英文' },
            { min: 8, message: '密码长度最少8位，最多不得超过20位' },
            { max: 20, message: '密码长度最少8位，最多不得超过20位' },
          ]}
        >
          <Input.Password
            className="login-bind-form-input"
            placeholder="请输入密码，需包含字母和数字，长度 8-20 位"
          />
        </Form.Item>
        <button type="button" className="login-bind-confirm-btn" onClick={handleResetPass}>
          确认密码
        </button>
        <button type="button" className="login-bind-cancel-btn" onClick={handleCloseResetPassModal}>
          取消
        </button>
      </Form>
    );
  };

  const handleBindAccount = async () => {
    try {
      await bindForm.validateFields();
    } catch {
      return;
    }
    const { emailOrPhone, verify_code, password } = bindForm.getFieldsValue();
    const account = emailOrPhone?.trim();
    const isEmail = emailPattern.test(account);
    const isPhone = phonePattern.test(account);
    try {
      const res = await loginBindWx({
        sid: wxObj?.sid,
        ...(isEmail ? { email: account } : {}),
        ...(isPhone ? { phone_number: account, country_code: '+86' } : {}),
        type: 'bind',
        ...(bindIsVerifyCode ? { verify_code: verify_code?.trim() } : { password: password?.trim() }),
      });
      if (res) {
        handleCloseBindModal();
        if (res?.user_reward) {
          const user_reword = res?.user_reward
          if (user_reword?.reward_event === 'register') {
            setSuccessOpen(true)
            setSuccessOpenObj({
              title: '注册成功！',
              type: 'register',
              text: `🎉恭喜解锁新手礼包，算力有效期${user_reword?.reward_duration}`,
              url: user_reword?.reward_url,
            });
          }
          if (user_reword?.reward_event === 'regress') {
            setSuccessOpen(true);
            setSuccessOpenObj({
              title: '欢迎回来！',
              type: 'regress',
              text: `🎉欢迎回来！送您专属算力礼包，算力有效期${user_reword?.reward_duration}`,
              url: user_reword?.reward_url,
            });
          }
        }
        loginSuccess(res);
      }
    } catch (error: any) {
      console.error(error);
      messageApi.open({ type: 'error', content: error });
    }
  };

  const handleBindVerifyCode = async () => {
    if (bindCountDown !== 60) {
      messageApi.open({ type: 'warning', content: `${bindCountDown}秒后再试` });
      return;
    }
    const emailOrPhone = bindForm.getFieldValue('emailOrPhone')?.trim();
    if (!emailOrPhone) {
      messageApi.open({ type: 'warning', content: '请填写手机号或邮箱' });
      return;
    }
    const isEmail = emailPattern.test(emailOrPhone);
    const isPhone = phonePattern.test(emailOrPhone);
    if (!isEmail && !isPhone) {
      messageApi.open({ type: 'warning', content: '请填写正确的手机号或邮箱' });
      return;
    }
    try {
      if (isEmail) {
        await getBindEmailCode({ email: emailOrPhone, code_type: 'login' });
        messageApi.open({ type: 'success', content: '验证码已发送，请注意查收' });
      } else {
        await getPhoneVerifyCode({
          phone_number: emailOrPhone,
          country_code: '+86',
          code_type: 'login',
        }).then(() => {
            messageApi.open({
              type: 'success',
              content: '短信验证码已发送，请注意查收',
            });
            setBindCountDown(59);
          })
          .catch(err => {
            if (err?.code === 5003) {
              messageApi.open({
                type: 'info',
                content: err?.err_msg || '联系客服获取验证码',
              });
            } else {
              messageApi.open({
                type: 'error',
                content: err?.err_msg || '发送验证码失败，请稍后再试',
              });
            }
          })
      }
      
    } catch (error: any) {
      console.error(error);
      messageApi.open({ type: 'error', content: error });
    }
  };

  const handleCloseBindModal = () => {
    setBindAccountVisible(false);
    setBindModalStep('choice');
    setBindIsVerifyCode(true);
    setBindCountDown(60);
    bindForm.resetFields();
  };

  const handleBindRegisterNew = () => {
    handleCloseBindModal();
    setIsWxRegister(true);
    setIsWX(true);
  };

  const handleCheck = (e: { target: { checked: boolean } }) => {
    setChecked(e.target.checked);
    localStorage.setItem('rememberPassword', String(e.target.checked));
  };

  const jumpRegister = (e: React.MouseEvent) => {
    e.preventDefault();
    propsSetIsLogin?.('register');
  };

  const handleForgetPassword = () => {
    propsSetIsLogin?.('forgetpassword');
  };

  const renderContent = (
    <div className='register-qrcode-alert'>
      <span className='register-qrcode-title'>{'扫码添加客服'}</span>
      <span className='register-qrcode-text' style={{ marginBottom: '12px' }}>{'获取注册邀请码，解锁更多使用权益'}</span>
      <img className='register-qrcode-image' src={QR_CODE} alt="qr-code" />
    </div>
  );

  const SECRET_INFO = 'https://mi8elatzm1x.feishu.cn/docx/PazXdOKmSordWGxxejLc2joqnIh?from=from_copylink';
  const USER_INFO = 'https://mi8elatzm1x.feishu.cn/docx/IyM2dSZKpovGFaxtws9ckeJFnSf?from=from_copylink';

  const handleJumpUser = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    window.open(USER_INFO, '_blank');
  };

  const handleJumpSecret = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    window.open(SECRET_INFO, '_blank');
  };

  const renderWXQrcode = () => {
    if (!qrcodeStatus) {
      return <div className="login-wx-qrcode-placeholder" />;
    }

    if (qrcodeStatus === 'pending') {
      return (
        <img className="login-wx-qrcode-img" src={wxObj?.base64} alt="wx-qrcode" />
      );
    }

    if (qrcodeStatus === 'success' || qrcodeStatus === 'need_bind') {
      return (
        <div className="login-wx-qrcode-container">
          <img className="login-wx-qrcode-img" src={wxObj?.base64} alt="wx-qrcode" />
          <div className="login-item-wx-qrcode-wrapper">
            <img className="login-wx-qrcode-success-icon" src={SUCCESS} alt="success" />
            <span className="login-wx-qrcode-overlay-success-text">扫码成功！</span>
            <div className="login-item-wx-qrcode-wrapper-button" onClick={debounceGetNewQrcode}>
              <span>刷新</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="login-wx-qrcode-container">
        <img className="login-wx-qrcode-img" src={wxObj?.base64} alt="wx-qrcode" />
        <div className="login-item-wx-qrcode-wrapper">
          <ReloadOutlined className="login-wx-qrcode-reload-icon" />
          <span className="login-wx-qrcode-overlay-error-text">
            {qrcodeStatus === 'invalid' ? '二维码已失效，请重新获取' : '获取二维码出错，请重新获取二维码'}
          </span>
          <div className="login-item-wx-qrcode-wrapper-button" onClick={debounceGetNewQrcode}>
            <span>刷新</span>
          </div>
        </div>
      </div>
    );
  };

  const renderWXSection = () => (
    <div className="login-wx-section">
      <div className="login-wx-qrcode-card">
        {renderWXQrcode()}
      </div>
      <div className="login-wx-qrcode-tip">
        <img src={WEIXIN} alt="weixin" />
        <span>
          打开<span className="login-wx-qrcode-tip-highlight">微信</span>扫一扫登录
        </span>
      </div>
      <div className="login-wx-agreement">
        登录即代表同意
        <span className="login-wx-agreement-link" onClick={handleJumpUser}>用户协议</span>
        与
        <span className="login-wx-agreement-link" onClick={handleJumpSecret}>隐私政策</span>
      </div>
      <div className="login-wx-footer-tip">初始使用时，将通过短信验证码绑定手机号</div>
    </div>
  );

  const renderBindModalContent = () => {
    if (bindModalStep === 'choice') {
      return (
        <>
          <div className="login-bind-modal-title">绑定已有账号</div>
          <div className="login-bind-modal-desc">
            如果您是老用户，绑定已有账号后，
            <br />
            可直接使用微信/手机号/邮箱进行登录，
            <br />
            实现账号互通
          </div>
          <button
            type="button"
            className="login-bind-modal-btn-primary"
            onClick={() => setBindModalStep('form')}
          >
            我是老用户，立即绑定已有账号
          </button>
          <button
            type="button"
            className="login-bind-modal-btn-secondary"
            onClick={handleBindRegisterNew}
          >
            我要注册新账号
          </button>
        </>
      );
    }

    return (
      <>
        <div className="login-bind-modal-title">绑定已有账号</div>
        <Form form={bindForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="emailOrPhone"
            rules={[
              { required: true, message: '请输入手机号或邮箱' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const isEmail = emailPattern.test(value);
                  const isPhone = phonePattern.test(value);
                  if (!isEmail && !isPhone) {
                    return Promise.reject(new Error('请输入正确的手机号或邮箱'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input className="login-bind-form-input" autoComplete="new-password" placeholder="请输入您的手机号/邮箱" />
          </Form.Item>
          {bindIsVerifyCode ? (
            <Form.Item
              name="verify_code"
              rules={[
                { required: true, message: '请填写验证码' },
                { len: 6, message: '请输入6位验证码' },
              ]}
            >
              <Input
                className="login-bind-form-input"
                placeholder="请输入验证码"
                suffix={
                  bindCountDown === 60 ? (
                    <span className="login-bind-get-code" onClick={handleBindVerifyCode}>
                      获取验证码
                    </span>
                  ) : (
                    <span className="login-bind-get-code disabled">{bindCountDown}(s)后再试</span>
                  )
                }
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="password"
              rules={[
                { required: true, message: '请填写密码' },
                { pattern: passwordPattern, message: '密码需要包含数字和英文' },
                { min: 8, message: '密码长度最少8位' },
                { max: 20, message: '密码长度最多20位' },
              ]}
            >
              <Input.Password className="login-bind-form-input" autoComplete="new-password" placeholder="请输入密码" />
            </Form.Item>
          )}
          <div className="login-bind-form-links">
            <span
              className="login-bind-form-link"
              onClick={() => setBindIsVerifyCode((flag) => !flag)}
            >
              {bindIsVerifyCode ? '密码登录' : '验证码登录'}
            </span>
            {bindIsVerifyCode && (
              <Popover className="cant-hold-code" content={renderContent} trigger="click">
                <span className="login-bind-form-link-muted">收不到验证码？</span>
              </Popover>
            )}
          </div>
          <button type="button" className="login-bind-confirm-btn" onClick={handleBindAccount}>
            确认绑定
          </button>
          <button
            type="button"
            className="login-bind-cancel-btn"
            onClick={() => {
              setBindModalStep('choice');
              setBindIsVerifyCode(true);
              bindForm.resetFields();
            }}
          >
            取消
          </button>
          <div className="login-bind-form-notes">
            <p>· 绑定已有账号后，可进行微信/手机号/邮箱账号互通登录</p>
            <p>· 跳过后，将使用微信注册新账号，无法再关联旧账号</p>
          </div>
        </Form>
      </>
    );
  };

  const showAccountForm = !isWX || (isWX && isWxRegister);

  const renderInviteCode = () => {
    if (!isVerifyCode) {
      return 
    }
    if (!showInviteCode) {
      return (
          <span
            style={{ color: '#376BFF', cursor: 'pointer' }}
            onClick={() => setShowInviteCode(true)}
          >
            填写注册邀请码
          </span>
      )
    } else {
      return (
        <Flex align="center" gap={8}>
          <span>邀请码</span>
          <Form.Item name="invite_code" noStyle>
            <Input placeholder="请输入邀请码" style={{ maxWidth: '120px', height: '32px' }} maxLength={8} />
          </Form.Item>
        </Flex>
      )
    }
  }

  return (
    <>
      {contextHolder}
      <Form
        className="header-model-form"
        {...formItemLayout}
        form={form}
        variant={'filled'}
        size={'large'}
        initialValues={{
          emailOrPhone: '',
          password: '',
          verify_code: '',
          invite_code: '',
        }}
      >
        <Form.Item style={{ marginBottom: '24px' }}>
          <div className="header-model-form-item-title">
            <img style={{ width: '140px', height: '58px' }} src={LOGIN_LOGO} alt="login-logo" />
            <div className="header-model-form-item-title-text-container">
              <div className={`header-model-form-item-title-text ${!isWX ? 'header-model-form-item-title-active' : ''}`}>
                <span onClick={() => setIsWX(false)}>账号登录</span>
              </div>
              <div className="header-model-form-item-divider"></div>
              <div className={`header-model-form-item-title-text ${isWX ? 'header-model-form-item-title-active' : ''}`}>
                <span onClick={() => setIsWX(true)}>微信登录</span>
              </div>
            </div>
          </div>
        </Form.Item>

        {isWxRegister && (
          <Form.Item style={{ marginBottom: '12px' }}>
            <Flex justify="center">
              <span style={{ fontSize: '17px', fontWeight: 600, color: '#191919' }}>还差一步，绑定手机号</span>
            </Flex>
          </Form.Item>
        )}

        {showAccountForm ? (
          <>
            <Form.Item
              style={{ marginBottom: '12px' }}
              name="emailOrPhone"
              validateFirst
              rules={[
                { required: true, message: isWxRegister ? '请输入手机号' : '请输入手机号或邮箱' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const isEmail = emailPattern.test(value);
                    const isPhone = phonePattern.test(value);
                    if (isWxRegister && !isPhone) {
                      return Promise.reject(new Error('请输入正确的手机号'));
                    }
                    if (!isWxRegister && !isEmail && !isPhone) {
                      return Promise.reject(new Error('请输入正确的手机号或邮箱'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                style={{ height: '48px' }}
                placeholder={isWxRegister ? '请输入手机号' : '请输入手机号/邮箱'}
                autoComplete="new-password"
              />
            </Form.Item>

            {isVerifyCode ? (
              <Form.Item
                className="register-form-item-verify-code"
                style={{ marginBottom: '12px' }}
                name="verify_code"
                rules={[
                  { required: true, message: '请填写验证码' },
                  { len: 6, message: '请输入6位验证码' },
                ]}
              >
                <Space.Compact style={{ width: '100%' }}>
                  <Input placeholder="请输入验证码" autoComplete="new-password" style={{ height: '48px' }} />
                  {countDown === 60 ? (
                    <Button color="primary" variant="text" style={{ height: '48px' }} onClick={handleClickVerifyCode}>
                      获取验证码
                    </Button>
                  ) : (
                    <Button color="primary" variant="text" disabled style={{ height: '48px' }}>
                      {countDown + '(s)后再试'}
                    </Button>
                  )}
                </Space.Compact>
              </Form.Item>
            ) : (
              <Form.Item
                style={{ marginBottom: '12px' }}
                name="password"
                validateFirst
                rules={[
                  { required: true, message: '请填写密码' },
                  { pattern: passwordPattern, message: '密码需要包含数字和英文' },
                  { min: 8, message: '密码长度最少8位，最多不得超过20位' },
                  { max: 20, message: '密码长度最少8位，最多不得超过20位' },
                ]}
              >
                <Input.Password
                  placeholder="请输入密码"
                  autoComplete="new-password"
                  style={{ height: '48px' }}
                  iconRender={(visible) =>
                    visible ? (
                      <img className="password-icon" src={EyePng} alt="eye" />
                    ) : (
                      <img className="password-icon" src={HideEyePng} alt="hide-eye" />
                    )
                  }
                />
              </Form.Item>
            )}

            <Flex justify="space-between" align="center" className="forgot-password">
              {
                !isWxRegister
                  ? (
                    <span className="is-password-change" onClick={() => setIsVerifyCode((flag) => !flag)}>
                      {isVerifyCode ? '切换为密码登录' : '切换为验证码登录'}
                    </span>
                  )
                  : <span></span>
              }

              {isVerifyCode ? (
                <Popover className="cant-hold-code" content={renderContent} trigger="click">
                  <div>收不到验证码？</div>
                </Popover>
              ) : (
                <Flex align="center" gap={16}>
                  <Checkbox checked={checked} onChange={handleCheck} className="login-remember-password">
                    <span>记住密码</span>
                  </Checkbox>
                  {!isWxRegister && (
                    <span className="login-forgot-password" onClick={handleForgetPassword}>忘记密码？</span>
                  )}
                </Flex>
              )}
            </Flex>

            <Flex justify="end" align="center" className="forgot-password" style={{ marginBottom: '12px' }}>
              {renderInviteCode()}
            </Flex>

            <div className="header-model-footer">
              <Button type="primary" block onClick={handleLogin}>
                {isWxRegister ? '注册' : '登录'}
              </Button>
            </div>
          </>
        ) : (
          <Form.Item style={{ marginBottom: 0 }}>
            {renderWXSection()}
          </Form.Item>
        )}

        {showAccountForm && (
          <>
            <div className="login-account-agreement">
              登录即代表同意
              <span className="login-account-agreement-link" style={{ fontWeight: 500 }} onClick={handleJumpUser}>用户协议</span>
              与
              <span className="login-account-agreement-link" style={{ fontWeight: 500 }} onClick={handleJumpSecret}>隐私政策</span>
            </div>
            {!isWxRegister 
              ? (
                <div className="login-account-footer-tip">
                  未注册的手机号、邮箱，通过验证后将自动注册账号
                </div>
              )
              : (
                <div className="login-account-footer-tip">
                  初始使用时，将通过短信验证码绑定手机号
                </div>
              )
            }
          </>
        )}
        
        {/* <div className="header-model-footer-sign" style={{ marginBottom: '10px', marginTop: '24px' }}>
          <span style={{ color: '#888888' }}>还没有账号？</span>
          <span style={{ borderBottom: '1px solid #376BFF' }}>
            <a href="" style={{ color: '#376BFF' }} onClick={jumpRegister}>去注册</a>
          </span>
        </div> */}
      </Form>

      <Modal
        className="login-bind-modal login-reset-pass-modal"
        width={380}
        title={null}
        footer={null}
        open={resetPassVisible}
        onCancel={handleCloseResetPassModal}
        maskClosable={false}
        destroyOnClose
        centered
      >
        {renderResetPassModalContent()}
      </Modal>

      <Modal
        className="login-bind-modal"
        width={380}
        title={null}
        footer={null}
        open={bindAccountVisible}
        onCancel={handleCloseBindModal}
        maskClosable={false}
        destroyOnClose
        centered
      >
        {renderBindModalContent()}
      </Modal>
      {/* 注册或者回归奖励 */}
      <Modal
        className="register-success-modal2"
        width={380}
        title={null}
        footer={null}
        open={successOpen}
        maskClosable={false}
        centered
        onCancel={() => setSuccessOpen(false)}
        style={{ zIndex: 1001 }}
      >
        <div className="register-success-modal2-content">
          {
            successOpenObj?.type === 'regress'
              ? <></>
              : (
                <div className="register-success-modal2-header">
                  <img className="register-success-modal2-icon" src={REGISTER_SUCCESS} alt="success" />
                  <div className="register-success-modal2-main-title" style={{ marginLeft: '12px' }}>{successOpenObj?.title}</div>
                </div>
              )
          }
          <div className="register-success-modal2-body">
            <div className="register-success-modal2-desc">{successOpenObj?.text}</div>
            <div className="register-success-modal2-reward">
              <img style={{ width: '140px', height: 'auto' }} src={successOpenObj?.url} alt="reward" />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Login;
